import { PropsWithChildren, useEffect } from 'react';

import { useShopProvider } from '@givebacks/shop';
import { useAtom } from 'jotai';
import { Route, Routes } from 'react-router-dom';
import { Button, Stack, Text } from 'ui';

import {
  CrowdfundingProvider,
  StoreProvider,
  storeCauseIdAtom,
  useCrowdfundingProvider,
} from '../../Context';
import { SelectStoreItemModalFlow } from '../../components/organisms/modals/SelectStoreItemModalFlow';
import { CampaignsRoutes } from './donor';
import { CampaignsManagementRoutes } from './management';

type Props = {
  CausePage?: React.JSXElementConstructor<Record<string, unknown>>;
  children?: React.ReactNode;
  EventMap?: React.JSXElementConstructor<Record<string, unknown>>;
  basePath: string;
};

const StoreItemPurchaseModalProvider = ({ children }: { children: React.ReactNode }) => {
  const { causeId, campaignId } = useCrowdfundingProvider();
  const { isAddToCartAvailable } = useShopProvider();

  return (
    <SelectStoreItemModalFlow
      storeId={causeId}
      storeItemId={campaignId}
      isAddToCartAvailable={isAddToCartAvailable}
    >
      {children}
    </SelectStoreItemModalFlow>
  );
};

const CampaignPreviewEnd = () => {
  return (
    <Stack align="center" justify="center" h="100vh">
      <Text>This is the end of the campaign preview, please close this window</Text>
      <Button onClick={() => window.close()}>Close</Button>
    </Stack>
  );
};

const CampaignDonorRouter = ({ EventMap, basePath }: Props) => {
  return (
    <CrowdfundingProvider>
      <StoreItemPurchaseModalProvider>
        <Routes>
          <Route path=":uuid/*">
            <Route
              path="events/*"
              element={
                <CampaignsRoutes EventMap={EventMap} isEventRoute={true} basePath={basePath} />
              }
            />
            <Route path="campaigns/*" element={<CampaignsRoutes basePath={basePath} />} />
            <Route path="*" element={<CampaignPreviewEnd />} />
            <Route index element={<CampaignPreviewEnd />} />
          </Route>
          <Route path="*" element={<CampaignPreviewEnd />} />
        </Routes>
      </StoreItemPurchaseModalProvider>
    </CrowdfundingProvider>
  );
};

type CampaignSupporterProviderProp = PropsWithChildren;

/** Provider for crowdfunding to work with supporter routes */
const CampaignSupporterProvider = ({ children }: CampaignSupporterProviderProp) => {
  return (
    <CrowdfundingProvider>
      <StoreItemPurchaseModalProvider>{children}</StoreItemPurchaseModalProvider>
    </CrowdfundingProvider>
  );
};

/** Sets the cause id for campaign management */
const CampaignManagementProviderCauseSetter = ({
  causeId,
  children,
}: {
  causeId?: string;
  children: React.ReactNode;
}) => {
  const [cause_id, setCauseId] = useAtom(storeCauseIdAtom);
  useEffect(() => {
    if (causeId) {
      if (causeId !== cause_id) {
        setCauseId(causeId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [causeId]);
  return children;
};

/** Provider for crowdfunding to work with management routes, sets the cause id for the management routes*/
const CampaignManagementProvider = ({
  children,
  causeId,
}: CampaignSupporterProviderProp & { causeId?: string }) => {
  return (
    <StoreProvider>
      <CrowdfundingProvider>
        <CampaignManagementProviderCauseSetter causeId={causeId}>
          {children}
        </CampaignManagementProviderCauseSetter>
      </CrowdfundingProvider>
    </StoreProvider>
  );
};

const CampaignManagementRouter = ({ causeId }: { causeId?: string }) => {
  return (
    <CampaignManagementProvider causeId={causeId}>
      <CampaignsManagementRoutes />
    </CampaignManagementProvider>
  );
};

export {
  CampaignDonorRouter,
  CampaignManagementProvider,
  CampaignManagementRouter,
  CampaignSupporterProvider,
  CampaignsRoutes,
};
